import React, { useEffect, useState } from 'react';
import { getMDTReportsByPatient } from "../services/mdtReportService";
import { Eye, Sparkles, FileText } from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { MDTReport } from '../types';
import { getReportSummarySnippet } from '../utils/reportUtils';

interface MDTReportsTabProps {
  getStatusColor: (status: string) => string;
}

const MDTReportsTab: React.FC<MDTReportsTabProps> = ({ 
  getStatusColor,
}) => {
  const { id } = useParams<{ id: string }>();
  const [reports, setReports] = useState<MDTReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const reportsData = await getMDTReportsByPatient(id);
        setReports(reportsData);
      } catch (err) {
        console.error(err);
        setError("Failed to load MDT reports");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [id]);

  if (loading) return <p className="p-4 text-gray-500">Loading reports...</p>;
  if (error) return <p className="p-4 text-red-500">{error}</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <FileText className="h-5 w-5 text-primary-600" />
          <span>MDT Reports</span>
        </h3>
      </div>

      <div className="space-y-4">
        {reports.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">
            No MDT reports generated for this patient yet.
          </div>
        ) : (
          reports.map((report) => {
            const snippet = getReportSummarySnippet(report.context, 160);

            return (
              <div key={report.id} className="card p-5 border border-gray-200 hover:border-primary-300 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center space-x-3">
                      <h4 className="font-bold text-gray-900 text-base">MDT Report #{report.id}</h4>
                    </div>

                    <p className="text-xs text-gray-600">
                      Generated by <span className="font-medium text-gray-800">{report?.created_by?.name || 'Doctor'}</span>
                      {' • '}
                      {new Date(report?.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>

                    {snippet && (
                      <p className="text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100 italic line-clamp-2">
                        "{snippet}"
                      </p>
                    )}
                  </div>

                  <div className="flex flex-row md:flex-col items-end justify-between md:justify-center gap-2 flex-shrink-0">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wider ${getStatusColor(
                        report.status
                      )}`}
                    >
                      {report.status.replace('_', ' ')}
                    </span>

                    <button 
                      onClick={() => navigate(`/reports/${report.id}`, {
                        state: { 
                          from: "patient",
                          patientId: report?.patient?.patient_id || id
                        }
                      })}
                      className="btn-primary flex items-center space-x-2 text-xs"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View Report</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer Medical Disclaimer */}
      <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
        <span>
          AI-Assisted Clinical Decision Support — Multidisciplinary consultation reports. Requires clinician review.
        </span>
        <Link
          to="/legal?tab=medical-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 underline font-medium whitespace-nowrap"
        >
          Medical Disclaimer &rarr;
        </Link>
      </div>
    </div>
  );
};

export default MDTReportsTab;
